import React from 'react';
import { GoogleMap, useJsApiLoader, MarkerF, PolylineF } from '@react-google-maps/api';
import { Ship } from 'lucide-react';
import { useShipByImoWithPositions } from '@/core/api/queries';

const containerStyle = {
    width: '100%',
    height: '200px'
};

const mapOptions = {
    zoomControl: true,
    mapTypeControl: false,
    scaleControl: false,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: false,
    disableDefaultUI: false,
    gestureHandling: 'cooperative',
    styles: [
        {
            featureType: 'water',
            elementType: 'geometry',
            stylers: [{ color: '#bfdbfe' }] // pastel blue
        },
        {
            featureType: 'landscape',
            elementType: 'geometry',
            stylers: [{ color: '#f5f3ff' }] // pastel purple
        },
        {
            featureType: 'road',
            elementType: 'geometry',
            stylers: [{ color: '#e0e7ff' }] // pastel indigo
        }
    ]
};

interface ShipTrackingSimpleProps {
    imo: string;
}

const ShipTrackingSimple = ({ imo }: ShipTrackingSimpleProps) => {

    const { data: shipData, isLoading } = useShipByImoWithPositions(parseInt(imo), !!imo);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: 'AIzaSyBUfoGJxQTuhIXyfc3k2a9iRf32r8hr8Ug'
    });

    const getShipIcon = (heading: number) => {
        return {
            path: 'M12 2L4 12h16L12 2z',
            fillColor: '#93c5fd', // pastel blue
            fillOpacity: 0.8,
            strokeColor: '#ffffff',
            strokeWeight: 2,
            scale: 1.5,
            rotation: heading || 0,
            anchor: new window.google.maps.Point(12, 12)
        };
    };

    if (!isLoaded || isLoading) {
        return (
            <div className="relative w-full h-[200px] flex items-center justify-center bg-gray-50">
                <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 border-4 border-blue-300 border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-gray-600">Loading map...</span>
                </div>
            </div>
        );
    }

    if (!shipData?.ship) {
        return <div className="flex items-center justify-center h-[200px] text-gray-500">Ship not found</div>;
    }

    const { ship, positionHistory } = shipData;
    const center = ship.lastLat && ship.lastLon
        ? { lat: ship.lastLat, lng: ship.lastLon }
        : { lat: 0, lng: 0 };

    return (
        <div className="relative w-full h-48 border border-gray-200 rounded-lg overflow-hidden">
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={8}
                options={mapOptions}
            >
                {/* Current Position */}
                {ship.lastLat && ship.lastLon && (
                    <MarkerF
                        position={{ lat: ship.lastLat, lng: ship.lastLon }}
                        icon={getShipIcon(ship.lastHeading)}
                        title={ship.name}
                    />
                )}

                {/* Position History Trail */}
                {positionHistory.length > 0 && (
                    <PolylineF
                        path={positionHistory
                            .filter((pos: any) => pos.lat && pos.lng)
                            .map((pos: any) => ({ lat: pos.lat, lng: pos.lng }))}
                        options={{
                            strokeColor: '#c084fc', // pastel purple
                            strokeOpacity: 0.5,
                            strokeWeight: 2,
                            geodesic: true,
                        }}
                    />
                )}
            </GoogleMap>

            {/* Ship Info Card */}
            <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-lg shadow-md px-3 py-2 border border-gray-100">
                <div className="flex items-center gap-2">
                    <Ship size={16} className="text-blue-400" />
                    <div>
                        <h2 className="font-semibold text-sm">{ship.name}</h2>
                        <p className="text-xs text-gray-500">Current position</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShipTrackingSimple;